import { _ } from "svelte-i18n";
import { get } from "svelte/store";
import { assets } from "$app/paths";
import cfg from "$lib/config/config";

export type SizeGuide = {
  headers: Header[];
  rows: Record<string, string | string[]>[];
};

type Header = {
  title: string;
  rounded: boolean;
};

export type SizeGuideSize = string | string[];

export type SizeGuideRow = {
  rowLabel: string;
  regionSizes: SizeGuideSize[];
  cmSizes: SizeGuideSize[];
  inchSizes?: SizeGuideSize[];
};

export const howToMeasureRepresent = () => {
  const t = get(_);
  return {
    [t("hoodie")]: {
      info: [
        {
          title: t("Shoulder"),
          body: t("sizeGuide.howToMeasure.shoulder"),
        },
        {
          title: t("Chest Width"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("Front Length"),
          body: t("sizeGuide.howToMeasure.frontLength"),
        },
        {
          title: t("Sleeve Length"),
          body: t("sizeGuide.howToMeasure.sleeve"),
        },
        {
          title: t("Hem Width"),
          body: t("sizeGuide.howToMeasure.hem"),
        },
      ],
      image: `${assets}/represent-hoodie.svg`,
    },
    [t("t-shirt")]: {
      info: [
        {
          title: t("Shoulder"),
          body: t("sizeGuide.howToMeasure.shoulder"),
        },
        {
          title: t("Chest Width"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("Front Length"),
          body: t("sizeGuide.howToMeasure.frontLength"),
        },
        {
          title: t("Sleeve Length"),
          body: t("sizeGuide.howToMeasure.sleeve"),
        },
        {
          title: t("Hem Width"),
          body: t("sizeGuide.howToMeasure.hem"),
        },
      ],
      image: `${assets}/represent-tshirt.svg`,
    },
    [t("jacket")]: {
      info: [
        {
          title: t("Chest Width"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("Hem Width"),
          body: t("sizeGuide.howToMeasure.hem"),
        },
        {
          title: t("Sleeve Length"),
          body: t("sizeGuide.howToMeasure.sleeve"),
        },
        {
          title: t("Front Length"),
          body: t("sizeGuide.howToMeasure.frontLength"),
        },
        {
          title: t("Across Shoulder"),
          body: t("sizeGuide.howToMeasure.acrossShoulder"),
        },
      ],
      image: `${assets}/represent-jacket.svg`,
    },
    [t("parka")]: {
      info: [
        {
          title: t("Chest Width"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("Hem Width"),
          body: t("sizeGuide.howToMeasure.hem"),
        },
        {
          title: t("Sleeve Length"),
          body: t("sizeGuide.howToMeasure.sleeve"),
        },
        {
          title: t("Front Length"),
          body: t("sizeGuide.howToMeasure.frontLength"),
        },
        {
          title: t("Across Shoulder"),
          body: t("sizeGuide.howToMeasure.acrossShoulder"),
        },
      ],
      image: `${assets}/represent-parka.svg`,
    },

    [t("trousers")]: {
      info: [
        {
          title: t("Inside Leg Length"),
          body: t("sizeGuide.howToMeasure.insideLeg"),
        },
        {
          title: t("Waist Width"),
          body: t("sizeGuide.howToMeasure.waistWidth"),
        },
        {
          title: t("Hip Width"),
          body: t("sizeGuide.howToMeasure.hipWidth"),
        },
      ],
      image: `${assets}/represent-trousers.svg`,
    },
    [t("cargos")]: {
      info: [
        {
          title: t("Inside Leg Length"),
          body: t("sizeGuide.howToMeasure.insideLeg"),
        },
        {
          title: t("Waist Width"),
          body: t("sizeGuide.howToMeasure.waistWidth"),
        },
        {
          title: t("Hip Width"),
          body: t("sizeGuide.howToMeasure.hipWidth"),
        },
      ],
      image: `${assets}/represent-cargos.svg`,
    },

    [t("sweater")]: {
      info: [
        {
          title: t("Chest Width"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("Hem Width"),
          body: t("sizeGuide.howToMeasure.hem"),
        },
        {
          title: t("Sleeve Length"),
          body: t("sizeGuide.howToMeasure.sleeve"),
        },
        {
          title: t("Front Length"),
          body: t("sizeGuide.howToMeasure.frontLength"),
        },
        {
          title: t("Shoulder"),
          body: t("sizeGuide.howToMeasure.shoulder"),
        },
      ],
      image: `${assets}/represent-sweater.svg`,
    },
  };
};

export const howToMeasure = (gender: string) => {
  const t = get(_);
  return {
    [t("outerwear & tops")]: {
      info: [
        {
          title: t("chest"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("waist"),
          body: t("sizeGuide.howToMeasure.waist"),
        },
        {
          title: t("hip"),
          body: t("sizeGuide.howToMeasure.hip"),
        },
        {
          title: t("overarm"),
          body: t("sizeGuide.howToMeasure.overarm"),
        },
      ],
      image: `${assets}/${gender} - outerwear & tops.svg`,
    },
    [t("other outerwear")]: {
      info: [
        {
          title: t("chest"),
          body: t("sizeGuide.howToMeasure.chest"),
        },
        {
          title: t("waist"),
          body: t("sizeGuide.howToMeasure.waist"),
        },
        {
          title: t("hip"),
          body: t("sizeGuide.howToMeasure.hip"),
        },
        {
          title: t("overarm"),
          body: t("sizeGuide.howToMeasure.overarm"),
        },
      ],
      image: `${assets}/${gender} - outerwear & tops.svg`,
    },
    [t("trousers & denim")]: {
      info: [
        {
          title: t("Trouser Waist"),
          body: t("sizeGuide.howToMeasure.trouserWaist"),
        },
        {
          title: t("hip"),
          body: t("sizeGuide.howToMeasure.trouserHip"),
        },
        {
          title: t("Inside Leg"),
          body: t("sizeGuide.howToMeasure.insideLeg"),
        },
      ],
      image: `${assets}/${gender} - trousers & denim.svg`,
    },
    [t("belts")]: {
      info: [
        {
          title: t("Length"),
          body: t("sizeGuide.howToMeasure.belts"),
        },
      ],
      image: `${assets}/BELT.svg`,
    },
    [t("gloves")]: {
      info: [
        {
          title: t("Hand Circumference"),
          body: t("sizeGuide.howToMeasure.gloves"),
        },
      ],
      image: `${assets}/HAND.svg`,
    },
  };
};

const cmToInch = (cm: string) =>
  (parseFloat(cm) * 0.3937).toFixed(1).toString().replace(".0", "");
const inchToCm = (inch: string) =>
  (parseFloat(inch) * 2.54).toFixed(1).toString().replace(".0", "");

type SizeHeader = {
  title: string;
  rounded: boolean;
};
type SizeRow = {
  [key: string]: string | string[] | boolean;
};
type SizeBody = {
  headers: SizeHeader[];
  rows: SizeRow[];
};
type Size = {
  [key: string]: {
    [key: string]: SizeBody;
  };
};

export function getSizes(gender: string, type: string, unit = "cm") {
  const t = get(_);
  const sizes: Size = {
    men: {
      [t("outerwear & tops")]: {
        headers: [
          { title: t("Size"), rounded: true },
          { title: t("UK/US"), rounded: true },
          { title: t("EU/IT"), rounded: true },
          { title: `${t("Measurements")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "XS",
            [t("UK/US")]: "34",
            [t("EU/IT")]: "44",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "90" : cmToInch("90")}`,
              `${t("Waist")} - ${unit === "cm" ? "78.5" : cmToInch("78.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "91" : cmToInch("91")}`,
              `${t("Overarm")} - ${unit === "cm" ? "82.5" : cmToInch("82.5")}`,
            ],
          },
          {
            [t("Size")]: "S",
            [t("UK/US")]: "36 - 38",
            [t("EU/IT")]: "46 - 48",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "94 - 98"
                  : `${cmToInch("94")} - ${cmToInch("98")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "82.5 - 86.5"
                  : `${cmToInch("82.5")} - ${cmToInch("86.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "95 - 99"
                  : `${cmToInch("95")} - ${cmToInch("99")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "84 - 85.5"
                  : `${cmToInch("84")} - ${cmToInch("85.5")}`
              }`,
            ],
          },
          {
            [t("Size")]: "M",
            [t("UK/US")]: "38 - 40",
            [t("EU/IT")]: "48 - 50",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "98 - 102"
                  : `${cmToInch("98")} - ${cmToInch("102")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "86.5 - 90.5"
                  : `${cmToInch("86.5")} - ${cmToInch("90.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "99 - 103"
                  : `${cmToInch("99")} - ${cmToInch("103")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "85.5 - 87"
                  : `${cmToInch("85.5")} - ${cmToInch("87")}`
              }`,
            ],
          },
          {
            [t("Size")]: "L",
            [t("UK/US")]: "40 - 42",
            [t("EU/IT")]: "50 - 52",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "102 - 106"
                  : `${cmToInch("102")} - ${cmToInch("106")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "90.5 - 94.5"
                  : `${cmToInch("90.5")} - ${cmToInch("94.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "103 - 107"
                  : `${cmToInch("103")} - ${cmToInch("107")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "87 - 88.5"
                  : `${cmToInch("87")} - ${cmToInch("88.5")}`
              }`,
            ],
          },
          {
            [t("Size")]: "XL",
            [t("UK/US")]: "42 - 44",
            [t("EU/IT")]: "52 - 54",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "106 - 110"
                  : `${cmToInch("106")} - ${cmToInch("110")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "94.5 - 98.5"
                  : `${cmToInch("94.5")} - ${cmToInch("98.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "107 - 111"
                  : `${cmToInch("107")} - ${cmToInch("111")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "88.5 - 90"
                  : `${cmToInch("88.5")} - ${cmToInch("90")}`
              }`,
            ],
          },
          {
            [t("Size")]: "XXL",
            [t("UK/US")]: "44 - 46",
            [t("EU/IT")]: "54 - 56",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "110 - 114"
                  : `${cmToInch("110")} - ${cmToInch("114")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "98.5 - 102.5"
                  : `${cmToInch("98.5")} - ${cmToInch("102.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "111 - 115"
                  : `${cmToInch("111")} - ${cmToInch("115")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "90 - 91.5"
                  : `${cmToInch("90")} - ${cmToInch("91.5")}`
              }`,
            ],
          },
          {
            [t("Size")]: "3XL",
            [t("UK/US")]: "46 - 48",
            [t("EU/IT")]: "56 - 58",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "114 - 118"
                  : `${cmToInch("114")} - ${cmToInch("118")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "102.5 - 106.5"
                  : `${cmToInch("102")} - ${cmToInch("106.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "115 - 119"
                  : `${cmToInch("115")} - ${cmToInch("119")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "91.5 - 93"
                  : `${cmToInch("91.5")} - ${cmToInch("93")}`
              }`,
            ],
          },
          {
            [t("Size")]: "4XL",
            [t("UK/US")]: "48",
            [t("EU/IT")]: "58",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "118" : cmToInch("118")}`,
              `${t("Waist")} - ${unit === "cm" ? "106.5" : cmToInch("106.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "119" : cmToInch("119")}`,
              `${t("Overarm")} - ${unit === "cm" ? "93" : cmToInch("93")}`,
            ],
          },
          {
            [t("Size")]: "5XL",
            [t("UK/US")]: "50",
            [t("EU/IT")]: "60",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "122" : cmToInch("122")}`,
              `${t("Waist")} - ${unit === "cm" ? "110.5" : cmToInch("110.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "123" : cmToInch("123")}`,
              `${t("Overarm")} - ${unit === "cm" ? "94.5" : cmToInch("94.5")}`,
            ],
          },
          {
            ["divider"]: t("BIG & TALL - SIZE GUIDE"),
          },
          {
            [t("Size")]: "XXL",
            [t("UK/US")]: "48",
            [t("EU/IT")]: "58",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "122" : cmToInch("122")}`,
              `${t("Waist")} - ${unit === "cm" ? "110.5" : cmToInch("110.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "119" : cmToInch("119")}`,
              `${t("Overarm")} - ${unit === "cm" ? "91" : cmToInch("91")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "3XL",
            [t("UK/US")]: "50",
            [t("EU/IT")]: "60",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "126" : cmToInch("126")}`,
              `${t("Waist")} - ${unit === "cm" ? "114.5" : cmToInch("114.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "123" : cmToInch("123")}`,
              `${t("Overarm")} - ${unit === "cm" ? "91.5" : cmToInch("91.5")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "4XL",
            [t("UK/US")]: "52",
            [t("EU/IT")]: "62",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "130" : cmToInch("130")}`,
              `${t("Waist")} - ${unit === "cm" ? "118.5" : cmToInch("118.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "127" : cmToInch("127")}`,
              `${t("Overarm")} - ${unit === "cm" ? "92" : cmToInch("92")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "5XL",
            [t("UK/US")]: "54",
            [t("EU/IT")]: "64",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "134" : cmToInch("134")}`,
              `${t("Waist")} - ${unit === "cm" ? "122.5" : cmToInch("122.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "131" : cmToInch("131")}`,
              `${t("Overarm")} - ${unit === "cm" ? "92.5" : cmToInch("92.5")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "6XL",
            [t("UK/US")]: "56",
            [t("EU/IT")]: "66",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "138" : cmToInch("138")}`,
              `${t("Waist")} - ${unit === "cm" ? "126.5" : cmToInch("126.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "135" : cmToInch("135")}`,
              `${t("Overarm")} - ${unit === "cm" ? "93" : cmToInch("93")}`,
            ],
            bigAndTall: true,
          },
        ],
      },
      [t("other outerwear")]: {
        headers: [
          { title: t("UK/US"), rounded: true },
          { title: t("EU/IT"), rounded: true },
          { title: t("Size"), rounded: true },
          { title: `${t("Measurements")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "XS",
            [t("UK/US")]: "34",
            [t("EU/IT")]: "44",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "90" : cmToInch("90")}`,
              `${t("Waist")} - ${unit === "cm" ? "78.5" : cmToInch("78.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "91" : cmToInch("91")}`,
              `${t("Overarm")} - ${unit === "cm" ? "82.5" : cmToInch("82.5")}`,
            ],
          },
          {
            [t("Size")]: "XS - S",
            [t("UK/US")]: "36",
            [t("EU/IT")]: "46",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "94" : cmToInch("94")}`,
              `${t("Waist")} - ${unit === "cm" ? "82.5" : cmToInch("82.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "95" : cmToInch("95")}`,
              `${t("Overarm")} - ${unit === "cm" ? "84" : cmToInch("84")}`,
            ],
          },
          {
            [t("Size")]: "S - M",
            [t("UK/US")]: "38",
            [t("EU/IT")]: "48",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "98" : cmToInch("98")}`,
              `${t("Waist")} - ${unit === "cm" ? "86.5" : cmToInch("86.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "99" : cmToInch("99")}`,
              `${t("Overarm")} - ${unit === "cm" ? "85.5" : cmToInch("85.5")}`,
            ],
          },
          {
            [t("Size")]: "M - L",
            [t("UK/US")]: "40",
            [t("EU/IT")]: "50",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "102" : cmToInch("102")}`,
              `${t("Waist")} - ${unit === "cm" ? "90.5" : cmToInch("90.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "103" : cmToInch("103")}`,
              `${t("Overarm")} - ${unit === "cm" ? "87" : cmToInch("87")}`,
            ],
          },
          {
            [t("Size")]: "L - XL",
            [t("UK/US")]: "42",
            [t("EU/IT")]: "52",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "106" : cmToInch("106")}`,
              `${t("Waist")} - ${unit === "cm" ? "94.5" : cmToInch("94.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "107" : cmToInch("107")}`,
              `${t("Overarm")} - ${unit === "cm" ? "88.5" : cmToInch("88.5")}`,
            ],
          },
          {
            [t("Size")]: "XL - XXL",
            [t("UK/US")]: "44",
            [t("EU/IT")]: "54",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "110" : cmToInch("110")}`,
              `${t("Waist")} - ${unit === "cm" ? "98.5" : cmToInch("98.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "111" : cmToInch("111")}`,
              `${t("Overarm")} - ${unit === "cm" ? "90" : cmToInch("90")}`,
            ],
          },
          {
            [t("Size")]: "XXL - 3XL",
            [t("UK/US")]: "46",
            [t("EU/IT")]: "56",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "114" : cmToInch("114")}`,
              `${t("Waist")} - ${unit === "cm" ? "102.5" : cmToInch("102.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "115" : cmToInch("115")}`,
              `${t("Overarm")} - ${unit === "cm" ? "91.5" : cmToInch("91.5")}`,
            ],
          },
          {
            [t("Size")]: "3XL - 4XL",
            [t("UK/US")]: "48",
            [t("EU/IT")]: "58",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "118" : cmToInch("118")}`,
              `${t("Waist")} - ${unit === "cm" ? "106.5" : cmToInch("106.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "119" : cmToInch("119")}`,
              `${t("Overarm")} - ${unit === "cm" ? "93" : cmToInch("93")}`,
            ],
          },
          {
            [t("Size")]: "5XL",
            [t("UK/US")]: "50",
            [t("EU/IT")]: "60",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "122" : cmToInch("122")}`,
              `${t("Waist")} - ${unit === "cm" ? "110.5" : cmToInch("110.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "123" : cmToInch("123")}`,
              `${t("Overarm")} - ${unit === "cm" ? "94.5" : cmToInch("94.5")}`,
            ],
          },
          {
            ["divider"]: t("BIG & TALL - SIZE GUIDE"),
          },
          {
            [t("Size")]: "XXL",
            [t("UK/US")]: "48",
            [t("EU/IT")]: "58",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "122" : cmToInch("122")}`,
              `${t("Waist")} - ${unit === "cm" ? "110.5" : cmToInch("110.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "119" : cmToInch("119")}`,
              `${t("Overarm")} - ${unit === "cm" ? "91" : cmToInch("91")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "3XL",
            [t("UK/US")]: "50",
            [t("EU/IT")]: "60",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "126" : cmToInch("126")}`,
              `${t("Waist")} - ${unit === "cm" ? "114.5" : cmToInch("114.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "123" : cmToInch("123")}`,
              `${t("Overarm")} - ${unit === "cm" ? "91.5" : cmToInch("91.5")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "4XL",
            [t("UK/US")]: "52",
            [t("EU/IT")]: "62",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "130" : cmToInch("130")}`,
              `${t("Waist")} - ${unit === "cm" ? "118.5" : cmToInch("118.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "127" : cmToInch("127")}`,
              `${t("Overarm")} - ${unit === "cm" ? "92" : cmToInch("92")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "5XL",
            [t("UK/US")]: "54",
            [t("EU/IT")]: "64",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "134" : cmToInch("134")}`,
              `${t("Waist")} - ${unit === "cm" ? "122.5" : cmToInch("122.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "131" : cmToInch("131")}`,
              `${t("Overarm")} - ${unit === "cm" ? "92.5" : cmToInch("92.5")}`,
            ],
            bigAndTall: true,
          },
          {
            [t("Size")]: "6XL",
            [t("UK/US")]: "56",
            [t("EU/IT")]: "66",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "138" : cmToInch("138")}`,
              `${t("Waist")} - ${unit === "cm" ? "126.5" : cmToInch("126.5")}`,
              `${t("Hip")} - ${unit === "cm" ? "135" : cmToInch("135")}`,
              `${t("Overarm")} - ${unit === "cm" ? "93" : cmToInch("93")}`,
            ],
            bigAndTall: true,
          },
        ],
      },
      [t("trousers & denim")]: {
        headers: [
          { title: t("UK - Waist"), rounded: true },
          { title: t("Size"), rounded: true },
          { title: `${t("Measurements")} - ${unit}`, rounded: false },
          { title: `${t("Inside Leg")} - ${unit}`, rounded: true },
        ],
        rows: [
          {
            [t("UK - Waist")]: "28 - 29",
            [t("Size")]: "XS",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "76.5 - 79"
                  : `${cmToInch("76.5")} - ${cmToInch("79")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "89 - 91.5"
                  : `${cmToInch("89")} - ${cmToInch("91.5")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
          {
            [t("UK - Waist")]: "30 - 31",
            [t("Size")]: "S",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "81.5 - 84"
                  : `${cmToInch("81.5")} - ${cmToInch("84")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "94 - 96.5"
                  : `${cmToInch("94")} - ${cmToInch("96.5")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
          {
            [t("UK - Waist")]: "32",
            [t("Size")]: "M",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm" ? "86.5" : cmToInch("86.5")
              }`,
              `${t("Hip")} - ${unit === "cm" ? "99" : cmToInch("99")}`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
          {
            [t("UK - Waist")]: "33 - 34",
            [t("Size")]: "L",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "89 - 91.5"
                  : `${cmToInch("89")} - ${cmToInch("91.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "101.5 - 104"
                  : `${cmToInch("101.5")} - ${cmToInch("104")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
          {
            [t("UK - Waist")]: "35",
            [t("Size")]: "XL",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm" ? "94" : cmToInch("94")
              }`,
              `${t("Hip")} - ${unit === "cm" ? "106.5" : cmToInch("106.5")}`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
          {
            [t("UK - Waist")]: "36 - 37",
            [t("Size")]: "XXL",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "96.5 - 99"
                  : `${cmToInch("96.5")} - ${cmToInch("99")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "109 - 111.5"
                  : `${cmToInch("109")} - ${cmToInch("111.5")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
          {
            [t("UK - Waist")]: "38",
            [t("Size")]: "3XL",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm" ? "101.5" : cmToInch("101.5")
              }`,
              `${t("Hip")} - ${unit === "cm" ? "114" : cmToInch("114")}`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "81" : "32"}`,
              `${unit === "cm" ? "86" : "34"}`,
              `${unit === "cm" ? "91" : "36"}`,
            ],
          },
        ],
      },
      [t("footwear")]: {
        headers: [
          { title: t("UK"), rounded: true },
          { title: t("US"), rounded: true },
          { title: t("EU/IT"), rounded: true },
        ],
        rows: [
          {
            [t("UK")]: "6.5",
            [t("US")]: "7.5",
            [t("EU/IT")]: "40",
          },
          {
            [t("UK")]: "7",
            [t("US")]: "8",
            [t("EU/IT")]: "41",
          },
          {
            [t("UK")]: "7.5",
            [t("US")]: "8.5",
            [t("EU/IT")]: "41.5",
          },
          {
            [t("UK")]: "8",
            [t("US")]: "9",
            [t("EU/IT")]: "42",
          },
          {
            [t("UK")]: "8.5",
            [t("US")]: "9.5",
            [t("EU/IT")]: "42.5",
          },
          {
            [t("UK")]: "9",
            [t("US")]: "10",
            [t("EU/IT")]: "43",
          },
          {
            [t("UK")]: "9.5",
            [t("US")]: "10.5",
            [t("EU/IT")]: "43.5",
          },
          {
            [t("UK")]: "10",
            [t("US")]: "11",
            [t("EU/IT")]: "44",
          },
          {
            [t("UK")]: "10.5",
            [t("US")]: "11.5",
            [t("EU/IT")]: "44.5",
          },
          {
            [t("UK")]: "11",
            [t("US")]: "12",
            [t("EU/IT")]: "45",
          },
          {
            [t("UK")]: "12",
            [t("US")]: "13",
            [t("EU/IT")]: "46",
          },
        ],
      },
      [t("belts")]: {
        headers: [
          { title: t("Size"), rounded: true },
          { title: `${t("Length")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "S",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "80" : cmToInch("80")
            } - ${unit === "cm" ? "85" : cmToInch("85")}`,
          },
          {
            [t("Size")]: "M",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "90" : cmToInch("90")
            } - ${unit === "cm" ? "95" : cmToInch("95")}`,
          },
          {
            [t("Size")]: "L",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "100" : cmToInch("100")
            } - ${unit === "cm" ? "105" : cmToInch("105")}`,
          },
          {
            [t("Size")]: "XL",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "110" : cmToInch("110")
            } - ${unit === "cm" ? "120" : cmToInch("120")}`,
          },
        ],
      },
      [t("gloves")]: {
        headers: [
          { title: t("Size"), rounded: true },
          { title: `${t("Length")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "XS",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("7.5") : "7.5",
          },
          {
            [t("Size")]: "S",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("8") : "8",
          },
          {
            [t("Size")]: "M",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("8.5") : "8.5",
          },
          {
            [t("Size")]: "L",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("9") : "9",
          },
          {
            [t("Size")]: "XL",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("9.5") : "9.5",
          },
          {
            [t("Size")]: "XXL",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("10") : "10",
          },
          {
            [t("Size")]: "3XL",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("10.5") : "10.5",
          },
          {
            [t("Size")]: "4XL",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("11") : "11",
          },
          {
            [t("Size")]: "5XL",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("11.5") : "11.5",
          },
        ],
      },
    },
    women: {
      [t("outerwear & tops")]: {
        headers: [
          { title: t("Size"), rounded: true },
          { title: t("UK"), rounded: true },
          { title: t("US"), rounded: true },
          { title: t("DE"), rounded: true },
          { title: t("EU/IT"), rounded: true },
          { title: `${t("Measurements")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "XXS",
            [t("UK")]: "4 - 6",
            [t("US")]: "2 - 4",
            [t("DE")]: "30 - 32",
            [t("EU/IT")]: "36 - 38",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "78 - 82"
                  : `${cmToInch("78")} - ${cmToInch("82")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "60 - 64"
                  : `${cmToInch("60")} - ${cmToInch("64")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "86 - 90"
                  : `${cmToInch("86")} - ${cmToInch("90")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "75 - 76.5"
                  : `${cmToInch("75")} - ${cmToInch("76.5")}`
              }`,
            ],
          },
          {
            [t("Size")]: "XS",
            [t("UK")]: "6 - 8",
            [t("US")]: "4 - 6",
            [t("DE")]: "32 - 34",
            [t("EU/IT")]: "38 - 40",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "82 - 86"
                  : `${cmToInch("82")} - ${cmToInch("86")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "64 - 68"
                  : `${cmToInch("64")} - ${cmToInch("68")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "90 - 94"
                  : `${cmToInch("90")} - ${cmToInch("94")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "76.5 78"
                  : `${cmToInch("76.5")} - ${cmToInch("78")}`
              }`,
            ],
          },
          {
            [t("Size")]: "S",
            [t("UK")]: "8 - 10",
            [t("US")]: "6 - 8",
            [t("DE")]: "34 - 36",
            [t("EU/IT")]: "40 - 42",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "86 - 90"
                  : `${cmToInch("86")} - ${cmToInch("90")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "68 - 72"
                  : `${cmToInch("68")} - ${cmToInch("72")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "94 - 98"
                  : `${cmToInch("94")} - ${cmToInch("98")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "78 - 79.5"
                  : `${cmToInch("78")} - ${cmToInch("79.5")}`
              }`,
            ],
          },
          {
            [t("Size")]: "M",
            [t("UK")]: "10 - 12",
            [t("US")]: "8 - 10",
            [t("DE")]: "36 - 38",
            [t("EU/IT")]: "42 - 44",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "90 - 94"
                  : `${cmToInch("90")} - ${cmToInch("94")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "72 - 76"
                  : `${cmToInch("72")} - ${cmToInch("76")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "98 - 102"
                  : `${cmToInch("98")} - ${cmToInch("102")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "79.5 - 81"
                  : `${cmToInch("79.5")} - ${cmToInch("81")}`
              }`,
            ],
          },
          {
            [t("Size")]: "L",
            [t("UK")]: "12 - 14",
            [t("US")]: "10 - 12",
            [t("DE")]: "38 - 40",
            [t("EU/IT")]: "44 - 46",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "94 - 98"
                  : `${cmToInch("94")} - ${cmToInch("98")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "76 - 80"
                  : `${cmToInch("76")} - ${cmToInch("80")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "102 - 106"
                  : `${cmToInch("102")} - ${cmToInch("106")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "81 - 82.5"
                  : `${cmToInch("81")} - ${cmToInch("82.5")}`
              }`,
            ],
          },
          {
            [t("Size")]: "XL",
            [t("UK")]: "14 - 16",
            [t("US")]: "12 - 14",
            [t("DE")]: "42 - 44",
            [t("EU/IT")]: "46 - 48",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "98 - 102"
                  : `${cmToInch("98")} - ${cmToInch("102")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "80 - 84"
                  : `${cmToInch("80")} - ${cmToInch("84")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "106 - 110"
                  : `${cmToInch("106")} - ${cmToInch("110")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "82.5 - 84"
                  : `${cmToInch("82.5")} - ${cmToInch("84")}`
              }`,
            ],
          },
          {
            [t("Size")]: "XXL",
            [t("UK")]: "16 - 18",
            [t("US")]: "14 - 16",
            [t("DE")]: "44",
            [t("EU/IT")]: "48 - 50",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${
                unit === "cm"
                  ? "102 - 106"
                  : `${cmToInch("102")} - ${cmToInch("106")}`
              }`,
              `${t("Waist")} - ${
                unit === "cm"
                  ? "84 - 88"
                  : `${cmToInch("84")} - ${cmToInch("88")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "110 - 114"
                  : `${cmToInch("110")} - ${cmToInch("114")}`
              }`,
              `${t("Overarm")} - ${
                unit === "cm"
                  ? "84 - 85.5"
                  : `${cmToInch("84")} - ${cmToInch("85.5")}`
              }`,
            ],
          },
        ],
      },
      [t("other outerwear")]: {
        headers: [
          { title: t("UK"), rounded: true },
          { title: t("US"), rounded: true },
          { title: t("DE"), rounded: true },
          { title: t("EU/IT"), rounded: true },
          { title: t("Size"), rounded: true },
          { title: `${t("Measurements")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "XXS",
            [t("UK")]: "4",
            [t("US")]: "2",
            [t("DE")]: "30",
            [t("EU/IT")]: "36",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "78" : cmToInch("78")}`,
              `${t("Waist")} - ${unit === "cm" ? "60" : cmToInch("60")}`,
              `${t("Hip")} - ${unit === "cm" ? "86" : cmToInch("86")}`,
              `${t("Overarm")} - ${unit === "cm" ? "75" : cmToInch("75")}`,
            ],
          },
          {
            [t("Size")]: "XXS - XS",
            [t("UK")]: "6",
            [t("US")]: "4",
            [t("DE")]: "32",
            [t("EU/IT")]: "38",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "82" : cmToInch("82")}`,
              `${t("Waist")} - ${unit === "cm" ? "64" : cmToInch("64")}`,
              `${t("Hip")} - ${unit === "cm" ? "90" : cmToInch("90")}`,
              `${t("Overarm")} - ${unit === "cm" ? "76.5" : cmToInch("76.5")}`,
            ],
          },
          {
            [t("Size")]: "XS - S",
            [t("UK")]: "8",
            [t("US")]: "6",
            [t("DE")]: "34",
            [t("EU/IT")]: "40",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "86" : cmToInch("86")}`,
              `${t("Waist")} - ${unit === "cm" ? "68" : cmToInch("68")}`,
              `${t("Hip")} - ${unit === "cm" ? "94" : cmToInch("94")}`,
              `${t("Overarm")} - ${unit === "cm" ? "78" : cmToInch("78")}`,
            ],
          },
          {
            [t("Size")]: "S - M",
            [t("UK")]: "10",
            [t("US")]: "8",
            [t("DE")]: "36",
            [t("EU/IT")]: "42",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "90" : cmToInch("90")}`,
              `${t("Waist")} - ${unit === "cm" ? "72" : cmToInch("72")}`,
              `${t("Hip")} - ${unit === "cm" ? "98" : cmToInch("98")}`,
              `${t("Overarm")} - ${unit === "cm" ? "79.5" : cmToInch("79.5")}`,
            ],
          },
          {
            [t("Size")]: "M - L",
            [t("UK")]: "12",
            [t("US")]: "10",
            [t("DE")]: "38",
            [t("EU/IT")]: "44",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "94" : cmToInch("94")}`,
              `${t("Waist")} - ${unit === "cm" ? "76" : cmToInch("76")}`,
              `${t("Hip")} - ${unit === "cm" ? "102" : cmToInch("102")}`,
              `${t("Overarm")} - ${unit === "cm" ? "81" : cmToInch("81")}`,
            ],
          },
          {
            [t("Size")]: "L",
            [t("UK")]: "14",
            [t("US")]: "12",
            [t("DE")]: "40",
            [t("EU/IT")]: "46",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "98" : cmToInch("98")}`,
              `${t("Waist")} - ${unit === "cm" ? "80" : cmToInch("80")}`,
              `${t("Hip")} - ${unit === "cm" ? "106" : cmToInch("106")}`,
              `${t("Overarm")} - ${unit === "cm" ? "82.5" : cmToInch("82.5")}`,
            ],
          },
          {
            [t("Size")]: "XL",
            [t("UK")]: "16",
            [t("US")]: "14",
            [t("DE")]: "42",
            [t("EU/IT")]: "48",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Chest")} - ${unit === "cm" ? "102" : cmToInch("102")}`,
              `${t("Waist")} - ${unit === "cm" ? "84" : cmToInch("84")}`,
              `${t("Hip")} - ${unit === "cm" ? "110" : cmToInch("110")}`,
              `${t("Overarm")} - ${unit === "cm" ? "84" : cmToInch("84")}`,
            ],
          },
        ],
      },
      [t("trousers & denim")]: {
        headers: [
          { title: t("UK - Waist"), rounded: true },
          { title: t("Size"), rounded: true },
          { title: `${t("Measurements")} - ${unit}`, rounded: false },
          { title: `${t("Inside Leg")} - ${unit}`, rounded: true },
        ],
        rows: [
          {
            [t("UK - Waist")]: "23 - 24",
            [t("Size")]: "XXS",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "72 - 74.5"
                  : `${cmToInch("72")} -  ${cmToInch("74.5")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "84 - 86.5"
                  : `${cmToInch("84")} -  ${cmToInch("86.5")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "74" : "29"}`,
            ],
          },
          {
            [t("UK - Waist")]: "24 - 25",
            [t("Size")]: "XS",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "74.5 - 77"
                  : `${cmToInch("74.5")} -  ${cmToInch("77")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "86.5 - 89"
                  : `${cmToInch("86.5")} -  ${cmToInch("89")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "74" : "29"}`,
            ],
          },
          {
            [t("UK - Waist")]: "26 - 27",
            [t("Size")]: "S",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "79.5 - 82"
                  : `${cmToInch("79.5")} -  ${cmToInch("82")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "91.5 - 94"
                  : `${cmToInch("91.5")} -  ${cmToInch("94")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "74" : "29"}`,
            ],
          },
          {
            [t("UK - Waist")]: "28 - 29",
            [t("Size")]: "M",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "84.5 - 87"
                  : `${cmToInch("84.5")} -  ${cmToInch("87")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "96.5 - 99"
                  : `${cmToInch("96.5")} -  ${cmToInch("99")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "74" : "29"}`,
            ],
          },
          {
            [t("UK - Waist")]: "30 - 31",
            [t("Size")]: "L",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "89.5 - 92"
                  : `${cmToInch("89.5")} -  ${cmToInch("92")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "101.5 - 104"
                  : `${cmToInch("101.5")} -  ${cmToInch("104")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "74" : "29"}`,
            ],
          },
          {
            [t("UK - Waist")]: "32 - 33",
            [t("Size")]: "XL",
            [`${t("Measurements")} - ${unit}`]: [
              `${t("Trouser Waist")} - ${
                unit === "cm"
                  ? "94.5 - 97"
                  : `${cmToInch("94.5")} -  ${cmToInch("97")}`
              }`,
              `${t("Hip")} - ${
                unit === "cm"
                  ? "106.5 - 109"
                  : `${cmToInch("106.5")} -  ${cmToInch("109")}`
              }`,
            ],
            [`${t("Inside Leg")} - ${unit}`]: [
              `${unit === "cm" ? "74" : "29"}`,
            ],
          },
        ],
      },
      [t("footwear")]: {
        headers: [
          { title: t("UK"), rounded: true },
          { title: t("US"), rounded: true },
          { title: t("EU/IT"), rounded: true },
        ],
        rows: [
          {
            [t("UK")]: "2.5",
            [t("US")]: "5",
            [t("EU/IT")]: "35",
          },
          {
            [t("UK")]: "3",
            [t("US")]: "6",
            [t("EU/IT")]: "36",
          },
          {
            [t("UK")]: "4",
            [t("US")]: "7",
            [t("EU/IT")]: "37",
          },
          {
            [t("UK")]: "4.5",
            [t("US")]: "7.5",
            [t("EU/IT")]: "37.5",
          },
          {
            [t("UK")]: "5",
            [t("US")]: "8",
            [t("EU/IT")]: "38",
          },
          {
            [t("UK")]: "5.5",
            [t("US")]: "8.5",
            [t("EU/IT")]: "38.5",
          },
          {
            [t("UK")]: "6",
            [t("US")]: "9",
            [t("EU/IT")]: "40",
          },
          {
            [t("UK")]: "7",
            [t("US")]: "10",
            [t("EU/IT")]: "41",
          },
          {
            [t("UK")]: "7.5",
            [t("US")]: "10.5",
            [t("EU/IT")]: "41.5",
          },
          {
            [t("UK")]: "8",
            [t("US")]: "11",
            [t("EU/IT")]: "42",
          },
        ],
      },
      [t("belts")]: {
        headers: [
          { title: t("Size"), rounded: true },
          { title: `${t("Length")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "S",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "80" : cmToInch("80")
            } - ${unit === "cm" ? "85" : cmToInch("85")}`,
          },
          {
            [t("Size")]: "M",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "90" : cmToInch("90")
            } - ${unit === "cm" ? "95" : cmToInch("95")}`,
          },
          {
            [t("Size")]: "L",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "100" : cmToInch("100")
            } - ${unit === "cm" ? "105" : cmToInch("105")}`,
          },
          {
            [t("Size")]: "XL",
            [`${t("Length")} - ${unit}`]: `${
              unit === "cm" ? "110" : cmToInch("110")
            } - ${unit === "cm" ? "120" : cmToInch("120")}`,
          },
        ],
      },
      [t("gloves")]: {
        headers: [
          { title: t("Size"), rounded: true },
          { title: `${t("Length")} - ${unit}`, rounded: false },
        ],
        rows: [
          {
            [t("Size")]: "XS",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("7.5") : "7.5",
          },
          {
            [t("Size")]: "S",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("8") : "8",
          },
          {
            [t("Size")]: "M",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("8.5") : "8.5",
          },
          {
            [t("Size")]: "L",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("9") : "9",
          },
          {
            [t("Size")]: "XL",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("9.5") : "9.5",
          },
          {
            [t("Size")]: "XXL",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("10") : "10",
          },
          {
            [t("Size")]: "3XL",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("10.5") : "10.5",
          },
          {
            [t("Size")]: "4XL",
            [`${t("Length")} - ${unit}`]: unit === "cm" ? inchToCm("11") : "11",
          },
          {
            [t("Size")]: "5XL",
            [`${t("Length")} - ${unit}`]:
              unit === "cm" ? inchToCm("11.5") : "11.5",
          },
        ],
      },
    },
  };
  return sizes[gender][type] as SizeGuide;
}

export enum Gender {
  MEN = "men",
  WOMEN = "women",
}

export function getGender(gender: string): Gender {
  switch (gender) {
    case "men":
      return Gender.MEN;
    case "women":
      return Gender.WOMEN;
    default:
      return Gender.MEN;
  }
}

export function reorderSizes(
  gender: Gender,
  region: string,
  sizes: SizeGuide,
): SizeGuide {
  //For now just reorder womens
  const t = get(_);

  const { headers, rows } = sizes ?? {};

  type SizeOrders = {
    [Gender.MEN]: {
      [key: string]: string[];
    };
    [Gender.WOMEN]: {
      [key: string]: string[];
    };
  };

  const sizeOrders: SizeOrders = {
    [Gender.MEN]: {
      [t("UK")]: [t("UK"), t("IT"), t("EU/IT"), t("DE"), t("US")],
      [t("US")]: [t("US"), t("UK"), t("IT"), t("EU/IT"), t("DE")],
      [t("ROW")]: [t("US"), t("UK"), t("IT"), t("EU/IT"), t("DE")],
      [t("DE")]: [t("IT"), t("EU/IT"), t("DE"), t("UK"), t("US")],
      [t("EU")]: [t("IT"), t("EU/IT"), t("DE"), t("UK"), t("US")],
    },
    [Gender.WOMEN]: {
      [t("UK")]: [t("UK"), t("US"), t("UK/US"), t("EU/IT"), t("IT"), t("DE")],
      [t("US")]: [t("US"), t("UK"), t("UK/US"), t("EU/IT"), t("IT"), t("DE")],
      [t("ROW")]: [t("US"), t("UK"), t("UK/US"), t("EU/IT"), t("IT"), t("DE")],
      [t("DE")]: [t("IT"), t("DE"), t("EU/IT"), t("UK"), t("US"), t("UK/US")],
      [t("EU")]: [t("IT"), t("DE"), t("EU/IT"), t("UK"), t("US"), t("UK/US")],
    },
  };
  //filtering out the sizeOrder elements that don't exist on the headers
  const sizeOrderFilter = (el: string) => {
    return !!headers.find(h => h.title === el);
  };

  const sizeOrder =
    sizeOrders[gender][t(region.toUpperCase())]?.filter(sizeOrderFilter) ??
    sizeOrders[gender][t("UK")].filter(sizeOrderFilter); //default to UK

  //Need a custom sort as this order only accounts for regional sizes.
  //There could also be other sizing that don't need to be ordered (alpha or shoe sizes)
  //So we should leave them alone and order whats left around those that don't require ordering.
  const shifter = [...sizeOrder];
  const orderedHeaders = headers.map(el => {
    if (!sizeOrder.includes(el.title)) {
      return el;
    }
    //get the next size
    const next = shifter.shift();
    return headers.find(h => h.title === next);
  });

  //ordered headers should now be what is expected.
  return { headers: orderedHeaders, rows } as SizeGuide;
}

export function getRepresentSizes(category: string) {
  const t = get(_);
  const sizes = {
    [t("hoodie")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XXS",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 18`,
            `${t("Chest Width")} - 58`,
            `${t("Front Length")} - 70.5`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 39`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("18")}`,
            `${t("Chest Width")} - ${cmToInch("58")}`,
            `${t("Front Length")} - ${cmToInch("70.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("39")}`,
          ],
        },
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 18.45`,
            `${t("Chest Width")} - 59.25`,
            `${t("Front Length")} - 71`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 40.25`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("18.45")}`,
            `${t("Chest Width")} - ${cmToInch("59.25")}`,
            `${t("Front Length")} - ${cmToInch("71")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("40.25")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 18.9`,
            `${t("Chest Width")} - 59.5`,
            `${t("Front Length")} - 71.5`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 41.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("18.9")}`,
            `${t("Chest Width")} - ${cmToInch("59.5")}`,
            `${t("Front Length")} - ${cmToInch("71.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("41.5")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 19.8`,
            `${t("Chest Width")} - 63`,
            `${t("Front Length")} - 72.5`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 44`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("19.8")}`,
            `${t("Chest Width")} - ${cmToInch("63")}`,
            `${t("Front Length")} - ${cmToInch("72.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("44")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 21`,
            `${t("Chest Width")} - 66`,
            `${t("Front Length")} - 72.5`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 47`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("21")}`,
            `${t("Chest Width")} - ${cmToInch("66")}`,
            `${t("Front Length")} - ${cmToInch("72.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("47")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 22.2`,
            `${t("Chest Width")} - 69`,
            `${t("Front Length")} - 75.5`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 50`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("22.2")}`,
            `${t("Chest Width")} - ${cmToInch("69")}`,
            `${t("Front Length")} - ${cmToInch("75.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("50")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 23.4`,
            `${t("Chest Width")} - 72`,
            `${t("Front Length")} - 77`,
            `${t("Sleeve Length")} - 63`,
            `${t("Hem Width")} - 53`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("23.4")}`,
            `${t("Chest Width")} - ${cmToInch("72")}`,
            `${t("Front Length")} - ${cmToInch("77")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("53")}`,
          ],
        },
      ],
    },

    [t("t-shirt")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XXS",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 20.3`,
            `${t("Chest Width")} - 54.5`,
            `${t("Front Length")} - 71`,
            `${t("Sleeve Length")} - 19.75`,
            `${t("Hem Width")} - 54.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("20.3")}`,
            `${t("Chest Width")} - ${cmToInch("54.5")}`,
            `${t("Front Length")} - ${cmToInch("71")}`,
            `${t("Sleeve Length")} - ${cmToInch("19.75")}`,
            `${t("Hem Width")} - ${cmToInch("54.5")}`,
          ],
        },
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 20.3`,
            `${t("Chest Width")} - 55.75`,
            `${t("Front Length")} - 71.5`,
            `${t("Sleeve Length")} - 20.25`,
            `${t("Hem Width")} - 55.75`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("20.3")}`,
            `${t("Chest Width")} - ${cmToInch("55.75")}`,
            `${t("Front Length")} - ${cmToInch("71.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("20.25")}`,
            `${t("Hem Width")} - ${cmToInch("55.75")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 20.3`,
            `${t("Chest Width")} - 57`,
            `${t("Front Length")} - 72`,
            `${t("Sleeve Length")} - 20.75`,
            `${t("Hem Width")} - 57`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("20.3")}`,
            `${t("Chest Width")} - ${cmToInch("57")}`,
            `${t("Front Length")} - ${cmToInch("72")}`,
            `${t("Sleeve Length")} - ${cmToInch("20.75")}`,
            `${t("Hem Width")} - ${cmToInch("57")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 20.6`,
            `${t("Chest Width")} - 59.5`,
            `${t("Front Length")} - 73`,
            `${t("Sleeve Length")} - 21.25`,
            `${t("Hem Width")} - 59.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("20.6")}`,
            `${t("Chest Width")} - ${cmToInch("59.5")}`,
            `${t("Front Length")} - ${cmToInch("73")}`,
            `${t("Sleeve Length")} - ${cmToInch("21.25")}`,
            `${t("Hem Width")} - ${cmToInch("59.5")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 21`,
            `${t("Chest Width")} - 62.5`,
            `${t("Front Length")} - 74.5`,
            `${t("Sleeve Length")} - 22`,
            `${t("Hem Width")} - 62.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("21")}`,
            `${t("Chest Width")} - ${cmToInch("62.5")}`,
            `${t("Front Length")} - ${cmToInch("74.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("22")}`,
            `${t("Hem Width")} - ${cmToInch("62.5")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 21.4`,
            `${t("Chest Width")} - 65.5`,
            `${t("Front Length")} - 76`,
            `${t("Sleeve Length")} - 22.75`,
            `${t("Hem Width")} - 65.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("21.4")}`,
            `${t("Chest Width")} - ${cmToInch("65.5")}`,
            `${t("Front Length")} - ${cmToInch("76")}`,
            `${t("Sleeve Length")} - ${cmToInch("22.75")}`,
            `${t("Hem Width")} - ${cmToInch("65.5")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Shoulder")} - 21.8`,
            `${t("Chest Width")} - 68.5`,
            `${t("Front Length")} - 77.5`,
            `${t("Sleeve Length")} - 23.5`,
            `${t("Hem Width")} - 68.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Shoulder")} - ${cmToInch("21.8")}`,
            `${t("Chest Width")} - ${cmToInch("68.5")}`,
            `${t("Front Length")} - ${cmToInch("77.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("23.5")}`,
            `${t("Hem Width")} - ${cmToInch("68.5")}`,
          ],
        },
      ],
    },

    [t("jacket")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 56.25`,
            `${t("Hem Width")} - 58.75`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 68`,
            `${t("Across Shoulder")} - 57.15`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("56.25")}`,
            `${t("Hem Width")} - ${cmToInch("58.75")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("68")}`,
            `${t("Across Shoulder")} - ${cmToInch("57.15")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 57.5`,
            `${t("Hem Width")} - 60`,
            `${t("Sleeve Length")} - 61`,
            `${t("Front Length")} - 68.5`,
            `${t("Across Shoulder")} - 57.75`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("57.5")}`,
            `${t("Hem Width")} - ${cmToInch("60")}`,
            `${t("Sleeve Length")} - ${cmToInch("61")}`,
            `${t("Front Length")} - ${cmToInch("68.5")}`,
            `${t("Across Shoulder")} - ${cmToInch("57.75")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 60`,
            `${t("Hem Width")} - 62.5`,
            `${t("Sleeve Length")} - 62`,
            `${t("Front Length")} - 69.5`,
            `${t("Across Shoulder")} - 59`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("60")}`,
            `${t("Hem Width")} - ${cmToInch("62.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("62")}`,
            `${t("Front Length")} - ${cmToInch("69.5")}`,
            `${t("Across Shoulder")} - ${cmToInch("59")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 63`,
            `${t("Hem Width")} - 65.5`,
            `${t("Sleeve Length")} - 63`,
            `${t("Front Length")} - 71`,
            `${t("Across Shoulder")} - 60.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("63")}`,
            `${t("Hem Width")} - ${cmToInch("65.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("63")}`,
            `${t("Front Length")} - ${cmToInch("71")}`,
            `${t("Across Shoulder")} - ${cmToInch("60.5")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 66`,
            `${t("Hem Width")} - 68.5`,
            `${t("Sleeve Length")} - 64.5`,
            `${t("Front Length")} - 72.5`,
            `${t("Across Shoulder")} - 62`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("66")}`,
            `${t("Hem Width")} - ${cmToInch("68.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("64.5")}`,
            `${t("Front Length")} - ${cmToInch("72.5")}`,
            `${t("Across Shoulder")} - ${cmToInch("62")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 69`,
            `${t("Hem Width")} - 71.5`,
            `${t("Sleeve Length")} - 66`,
            `${t("Front Length")} - 74`,
            `${t("Across Shoulder")} - 63.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("69")}`,
            `${t("Hem Width")} - ${cmToInch("71.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("66")}`,
            `${t("Front Length")} - ${cmToInch("74")}`,
            `${t("Across Shoulder")} - ${cmToInch("63.5")}`,
          ],
        },
      ],
    },

    [t("parka")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XXS",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 61.5`,
            `${t("Hem Width")} - 59`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 72`,
            `${t("Across Shoulder")} - 56.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("61.5")}`,
            `${t("Hem Width")} - ${cmToInch("59")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("72")}`,
            `${t("Across Shoulder")} - ${cmToInch("56.5")}`,
          ],
        },
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 62.75`,
            `${t("Hem Width")} - 60.25`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 72.5`,
            `${t("Across Shoulder")} - 57.75`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("62.75")}`,
            `${t("Hem Width")} - ${cmToInch("60.25")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("72.5")}`,
            `${t("Across Shoulder")} - ${cmToInch("57.75")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 64`,
            `${t("Hem Width")} - 61.5`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 73`,
            `${t("Across Shoulder")} - 59`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("64")}`,
            `${t("Hem Width")} - ${cmToInch("61.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("73")}`,
            `${t("Across Shoulder")} - ${cmToInch("59")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 66.5`,
            `${t("Hem Width")} - 64`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 74`,
            `${t("Across Shoulder")} - 61.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("66.5")}`,
            `${t("Hem Width")} - ${cmToInch("64")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("74")}`,
            `${t("Across Shoulder")} - ${cmToInch("61.5")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 69.5`,
            `${t("Hem Width")} - 67`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 75.5`,
            `${t("Across Shoulder")} - 64.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("69.5")}`,
            `${t("Hem Width")} - ${cmToInch("67")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("75.5")}`,
            `${t("Across Shoulder")} - ${cmToInch("64.5")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 72.5`,
            `${t("Hem Width")} - 70`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 77`,
            `${t("Across Shoulder")} - 67.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("72.5")}`,
            `${t("Hem Width")} - ${cmToInch("70")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("77")}`,
            `${t("Across Shoulder")} - ${cmToInch("67.5")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 75.5`,
            `${t("Hem Width")} - 73`,
            `${t("Sleeve Length")} - 60.5`,
            `${t("Front Length")} - 78.5`,
            `${t("Across Shoulder")} - 70.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("75.5")}`,
            `${t("Hem Width")} - ${cmToInch("73")}`,
            `${t("Sleeve Length")} - ${cmToInch("60.5")}`,
            `${t("Front Length")} - ${cmToInch("78.5")}`,
            `${t("Across Shoulder")} - ${cmToInch("70.5")}`,
          ],
        },
      ],
    },

    [t("trousers")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XXS",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 78`,
            `${t("Waist Width- Relaxed")} - 35`,
            `${t("Hip Width")} - 40.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("78")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("35")}`,
            `${t("Hip Width")} - ${cmToInch("40.5")}`,
          ],
        },
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 78`,
            `${t("Waist Width- Relaxed")} - 36.25`,
            `${t("Hip Width")} - 51.74`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("78")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("36.25")}`,
            `${t("Hip Width")} - ${cmToInch("51.74")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 78`,
            `${t("Waist Width- Relaxed")} - 37.5`,
            `${t("Hip Width")} - 53`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("78")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("37.5")}`,
            `${t("Hip Width")} - ${cmToInch("53")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width- Relaxed")} - 40`,
            `${t("Hip Width")} - 55.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("40")}`,
            `${t("Hip Width")} - ${cmToInch("55.5")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width- Relaxed")} - 43`,
            `${t("Hip Width")} - 58.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("43")}`,
            `${t("Hip Width")} - ${cmToInch("58.5")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width- Relaxed")} - 46`,
            `${t("Hip Width")} - 61.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("46")}`,
            `${t("Hip Width")} - ${cmToInch("61.5")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width- Relaxed")} - 49`,
            `${t("Hip Width")} - 64.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width- Relaxed")} - ${cmToInch("49")}`,
            `${t("Hip Width")} - ${cmToInch("64.5")}`,
          ],
        },
      ],
    },
    [t("cargos")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XXS",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 78`,
            `${t("Waist Width")} - 38.5`,
            `${t("Hip Width")} - 48.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("78")}`,
            `${t("Waist Width")} - ${cmToInch("38.5")}`,
            `${t("Hip Width")} - ${cmToInch("48.5")}`,
          ],
        },
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 78`,
            `${t("Waist Width")} - 39.75`,
            `${t("Hip Width")} - 49.75`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("78")}`,
            `${t("Waist Width")} - ${cmToInch("39.75")}`,
            `${t("Hip Width")} - ${cmToInch("49.75")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 78`,
            `${t("Waist Width")} - 41`,
            `${t("Hip Width")} - 51`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("78")}`,
            `${t("Waist Width")} - ${cmToInch("41")}`,
            `${t("Hip Width")} - ${cmToInch("51")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width")} - 43.5`,
            `${t("Hip Width")} - 53.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width")} - ${cmToInch("43.5")}`,
            `${t("Hip Width")} - ${cmToInch("53.5")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width")} - 46.5`,
            `${t("Hip Width")} - 56.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width")} - ${cmToInch("46.5")}`,
            `${t("Hip Width")} - ${cmToInch("56.5")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width")} - 49.5`,
            `${t("Hip Width")} - 59.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width")} - ${cmToInch("49.5")}`,
            `${t("Hip Width")} - ${cmToInch("59.5")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Inside Leg Length")} - 79`,
            `${t("Waist Width")} - 52.5`,
            `${t("Hip Width")} - 62.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Inside Leg Length")} - ${cmToInch("79")}`,
            `${t("Waist Width")} - ${cmToInch("52.5")}`,
            `${t("Hip Width")} - ${cmToInch("62.5")}`,
          ],
        },
      ],
    },

    [t("sweater")]: {
      headers: [
        { title: t("Size"), rounded: true },
        { title: `${t("Measurements")} - cm`, rounded: false },
        { title: `${t("Measurements")} - inch`, rounded: false },
      ],
      rows: [
        {
          [t("Size")]: "XXS",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 56`,
            `${t("Hem Width")} - 55.5`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 64.5`,
            `${t("Shoulder")} - 22.3`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("56")}`,
            `${t("Hem Width")} - ${cmToInch("55.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("64.5")}`,
            `${t("Shoulder")} - ${cmToInch("22.3")}`,
          ],
        },
        {
          [t("Size")]: "XS",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 57.25`,
            `${t("Hem Width")} - 56.75`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 65`,
            `${t("Shoulder")} - 22.75`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("57.25")}`,
            `${t("Hem Width")} - ${cmToInch("56.75")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("65")}`,
            `${t("Shoulder")} - ${cmToInch("22.75")}`,
          ],
        },
        {
          [t("Size")]: "S",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 58.5`,
            `${t("Hem Width")} - 58`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 65.5`,
            `${t("Shoulder")} - 23.2`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("58.5")}`,
            `${t("Hem Width")} - ${cmToInch("58")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("65.5")}`,
            `${t("Shoulder")} - ${cmToInch("23.2")}`,
          ],
        },
        {
          [t("Size")]: "M",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 61`,
            `${t("Hem Width")} - 60.5`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 66.5`,
            `${t("Shoulder")} - 24.1`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("61")}`,
            `${t("Hem Width")} - ${cmToInch("60.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("66.5")}`,
            `${t("Shoulder")} - ${cmToInch("24.1")}`,
          ],
        },
        {
          [t("Size")]: "L",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 64`,
            `${t("Hem Width")} - 63.5`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 68`,
            `${t("Shoulder")} - 25.3`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("64")}`,
            `${t("Hem Width")} - ${cmToInch("63.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("68")}`,
            `${t("Shoulder")} - ${cmToInch("25.3")}`,
          ],
        },
        {
          [t("Size")]: "XL",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 67`,
            `${t("Hem Width")} - 66.5`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 69.5`,
            `${t("Shoulder")} - 26.5`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("67")}`,
            `${t("Hem Width")} - ${cmToInch("66.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("69.5")}`,
            `${t("Shoulder")} - ${cmToInch("26.5")}`,
          ],
        },
        {
          [t("Size")]: "XXL",
          [`${t("Measurements")} - cm`]: [
            `${t("Chest Width")} - 70`,
            `${t("Hem Width")} - 69.5`,
            `${t("Sleeve Length")} - 55`,
            `${t("Front Length")} - 71`,
            `${t("Shoulder")} - 27.7`,
          ],
          [`${t("Measurements")} - inch`]: [
            `${t("Chest Width")} - ${cmToInch("70")}`,
            `${t("Hem Width")} - ${cmToInch("69.5")}`,
            `${t("Sleeve Length")} - ${cmToInch("55")}`,
            `${t("Front Length")} - ${cmToInch("71")}`,
            `${t("Shoulder")} - ${cmToInch("27.7")}`,
          ],
        },
      ],
    },
  };
  return sizes[category];
}

export const sizeGuideMock: SizeGuide = {
  headers: [
    {
      title: "UK",
      rounded: true,
    },
    {
      title: "US",
      rounded: true,
    },
    {
      title: "EU/IT",
      rounded: true,
    },
  ],
  rows: [
    {
      UK: "6.5",
      US: "7.5",
      "EU/IT": "40",
    },
    {
      UK: "7",
      US: "8",
      "EU/IT": "41",
    },
    {
      UK: "7.5",
      US: "8.5",
      "EU/IT": "41.5",
    },
    {
      UK: "8",
      US: "9",
      "EU/IT": "42",
    },
    {
      UK: "8.5",
      US: "9.5",
      "EU/IT": "42.5",
    },
    {
      UK: "9",
      US: "10",
      "EU/IT": "43",
    },
    {
      UK: "9.5",
      US: "10.5",
      "EU/IT": "43.5",
    },
    {
      UK: "10",
      US: "11",
      "EU/IT": "44",
    },
    {
      UK: "10.5",
      US: "11.5",
      "EU/IT": "44.5",
    },
    {
      UK: "11",
      US: "12",
      "EU/IT": "45",
    },
    {
      UK: "12",
      US: "13",
      "EU/IT": "46",
    },
  ],
};
